// src/hooks/useDevMode.ts
import { useCallback, useEffect, useState } from "react";
import {
  adminLogin,
  clearAdminToken,
  getAdminToken,
  getAdminSessionInfo,
} from "@/lib/kbAdminClient";
import { clearDevViewportState } from "@/hooks/useDevViewport";

export type UseDevModeArgs = {
  // storage key you want to use (e.g. "bb-dev-mode")
  devModeKey: string;

  // LeadWorkspace wants these side effects
  setTrainingMode: (v: boolean) => void;
};

export type UseDevModeResult = {
  devMode: boolean;

  // modal state
  devLoginOpen: boolean;
  setDevLoginOpen: (v: boolean) => void;

  devLoginPassword: string;
  setDevLoginPassword: (v: string) => void;

  devLoginError: string;
  devLoginBusy: boolean;

  // actions
  requestDevMode: () => void;
  disableDevMode: (opts?: { trainingMode?: boolean }) => void;
  submitDevLogin: () => Promise<void>;

  // optional info (useful for UI)
  getSessionInfo: () => ReturnType<typeof getAdminSessionInfo>;
};

export function useDevMode({ devModeKey, setTrainingMode }: UseDevModeArgs): UseDevModeResult {
  const [devMode, setDevMode] = useState<boolean>(() => localStorage.getItem(devModeKey) === "true");

  const [devLoginOpen, setDevLoginOpen] = useState(false);
  const [devLoginPassword, setDevLoginPassword] = useState("");
  const [devLoginError, setDevLoginError] = useState("");
  const [devLoginBusy, setDevLoginBusy] = useState(false);

  const enableDevMode = useCallback(() => {
    localStorage.setItem(devModeKey, "true");
    setDevMode(true);
  }, [devModeKey]);

  const disableDevMode = useCallback(
    (opts?: { trainingMode?: boolean }) => {
      localStorage.removeItem(devModeKey);
      // Dev viewport preview (docs/planning/dev_mobile_viewport_preview.md
      // Q3): dev-only tools should not survive a dev-mode disable.
      // `clearDevViewportState` writes through the same path the impersonation
      // popover uses so the same-tab CustomEvent fires and every
      // `useDevViewport` instance (incl. DevViewportFrame at the app root)
      // unmounts the wrapper synchronously.
      try {
        clearDevViewportState();
      } catch {
        /* private mode / quota — ignore */
      }
      setDevMode(false);
      clearAdminToken();

      if (typeof opts?.trainingMode === "boolean") {
        setTrainingMode(opts.trainingMode);
      }
    },
    [devModeKey, setTrainingMode]
  );

  const requestDevMode = useCallback(() => {
    setDevLoginError("");
    setDevLoginPassword("");
    setDevLoginOpen(true);
  }, []);

  const submitDevLogin = useCallback(async () => {
    const password = devLoginPassword.trim();
    if (!password) {
      setDevLoginError("Password is required.");
      return;
    }

    setDevLoginBusy(true);
    setDevLoginError("");

    try {
      const r = await adminLogin(password);
      if (!r.ok) {
        setDevLoginError("Invalid developer password.");
        return;
      }

      // Preserve your current behavior:
      // - training mode on
      // - dev mode enabled
      setTrainingMode(true);
      enableDevMode();
      setDevLoginOpen(false);
    } finally {
      setDevLoginBusy(false);
    }
  }, [devLoginPassword, enableDevMode, setTrainingMode]);

  // 1) Once per browser session: force dev mode off + clear dev viewport.
  //
  // The `sessionStorage` gate was originally added to defuse a remount
  // race: `DevViewportFrame` used to restructure the DOM (fragment →
  // wrapping div) when the viewport activated, which unmounted and
  // remounted every descendant — including this hook. That remount re-ran
  // the clear effect right after the user committed a viewport via the
  // impersonation popover, which read as "flashed for half a second then
  // turned itself off". Dev-sync note 1c566b19-c5ac-4961-9a54-eaca75845ca9.
  //
  // Update (machine A): `DevViewportFrame` now renders the same React tree
  // shape in both states (display:contents when inactive), so the remount
  // no longer happens. The `sessionStorage` gate is kept anyway because it
  // also serves the original security intent: re-login required on a
  // fresh browser session.
  const ONCE_PER_SESSION_KEY = "bb-dev-mode-init-cleared";
  useEffect(() => {
    let alreadyCleared = false;
    try {
      alreadyCleared = window.sessionStorage.getItem(ONCE_PER_SESSION_KEY) === "1";
    } catch {
      /* sessionStorage may be unavailable (private mode) — treat as not-yet-cleared */
    }
    if (alreadyCleared) return;

    clearAdminToken();
    localStorage.removeItem(devModeKey);
    try {
      clearDevViewportState();
    } catch {
      /* private mode / quota — ignore */
    }
    setDevMode(false);

    try {
      window.sessionStorage.setItem(ONCE_PER_SESSION_KEY, "1");
    } catch {
      /* sessionStorage write may fail — accept the tradeoff (effect re-runs on remount in this tab) */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2) If devMode is on but admin token is missing/expired, just disable dev mode
  useEffect(() => {
    if (devMode && !getAdminToken()) {
      // Do not force training mode on here; preserve whatever the app already chose
      disableDevMode();
    }
  }, [devMode, disableDevMode]);

  return {
    devMode,

    devLoginOpen,
    setDevLoginOpen,

    devLoginPassword,
    setDevLoginPassword,

    devLoginError,
    devLoginBusy,

    requestDevMode,
    disableDevMode,
    submitDevLogin,

    getSessionInfo: () => getAdminSessionInfo(),
  };
}